import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import React from 'react';
import {Field, useForm} from '@tanstack/react-form';
import {useMutation} from '@tanstack/react-query';
import {fetch} from '../../shared/api/v1';
import type {components} from '../../shared/api/v1/openapi.types';
import {useMessage} from '../../providers/message/message-provider';
import {useAuth} from '../../providers/auth/auth-provider';
import {emailFormat, required} from '../../utils/validation';

type ConfirmEmailDto = components['schemas']['ConfirmEmailDto'];
type ResendEmailDto = components['schemas']['ResendEmailDto'];
type SetEmailDto = components['schemas']['SetEmailDto'];

type FormValues = ConfirmEmailDto;

type EmailStatusResponse = {
  email?: string;
  confirmed?: boolean;
  isConfirmed?: boolean;
  is_confirmed?: boolean;
  emailConfirmed?: boolean;
  email_confirmed?: boolean;
};

const resolveEmailStatus = (data: unknown) => {
  if (!data || typeof data !== 'object') {
    return {email: '', confirmed: false};
  }
  const typed = data as EmailStatusResponse;
  const confirmed =
    typed.confirmed ??
    typed.isConfirmed ??
    typed.is_confirmed ??
    typed.emailConfirmed ??
    typed.email_confirmed ??
    false;
  return {
    email: typed.email ?? '',
    confirmed: Boolean(confirmed),
  };
};

export const AccountTab = ({reloadKey}: { reloadKey: number }) => {
  const {showError, showMessage} = useMessage();
  const {token} = useAuth();
  const [cooldown, setCooldown] = React.useState(0);
  const [emailVerified, setEmailVerified] = React.useState(false);

  React.useEffect(() => {
    if (cooldown <= 0) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [cooldown]);

  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  const resendMutation = useMutation<void, Error, ResendEmailDto>({
    mutationFn: async (data: ResendEmailDto) => {
      if (!token) {
        throw new Error('Не удалось отправить код. Требуется авторизация.');
      }
      const response = await fetch.POST('/auth/email/resend', {
        body: data,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Не удалось отправить код');
      }
    },
    onSuccess: () => {
      showMessage({
        message: 'Код подтверждения отправлен на email',
      });
    },
    onError: (error) => {
      showError({
        message: error.message || 'Не удалось отправить код',
      });
    },
  });

  const setEmailMutation = useMutation<void, Error, SetEmailDto>({
    mutationFn: async (data: SetEmailDto) => {
      if (!token) {
        throw new Error('Не удалось обновить email. Требуется авторизация.');
      }
      const response = await fetch.POST('/auth/email', {
        body: data,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Не удалось обновить email');
      }
    },
    onSuccess: () => {
      showMessage({
        message: 'Email обновлён',
      });
      setEmailVerified(false);
    },
    onError: (error) => {
      showError({
        message: error.message || 'Не удалось обновить email',
      });
    },
  });

  const confirmMutation = useMutation<void, Error, ConfirmEmailDto>({
    mutationFn: async (data: ConfirmEmailDto) => {
      if (!token) {
        throw new Error('Не удалось подтвердить email. Требуется авторизация.');
      }
      const response = await fetch.POST('/auth/email/confirm', {
        body: data,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Не удалось подтвердить email');
      }
    },
    onSuccess: () => {
      showMessage({
        message: 'Email успешно подтверждён',
      });
      setEmailVerified(true);
    },
    onError: (error) => {
      showError({
        message: error.message || 'Не удалось подтвердить email',
      });
    },
  });

  const form = useForm({
    defaultValues: {
      email: '',
      code: '',
    } as FormValues,
    onSubmit: async ({value}) => {
      try {
        await confirmMutation.mutateAsync(value);
      } catch {
        // Errors are handled in onError
      }
    },
  });
  const Subscribe = form.Subscribe;

  const statusMutation = useMutation<{ email: string; confirmed: boolean }, Error>({
    mutationFn: async () => {
      if (!token) {
        throw new Error('Не удалось получить статус email. Требуется авторизация.');
      }
      const result = await fetch.GET('/auth/email/status', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const error = (result as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Не удалось получить статус email');
      }
      let data = (result as { data?: unknown }).data;
      if (!data && (result as { response?: Response }).response) {
        try {
          data = await (result as { response: Response }).response.clone().json();
        } catch {
          data = undefined;
        }
      }
      return resolveEmailStatus(data);
    },
    onSuccess: (data) => {
      form.setFieldValue('email', data.email);
      setEmailVerified(data.confirmed);
    },
    onError: (error) => {
      showError({
        message: error.message || 'Не удалось получить статус email',
      });
    },
  });

  const {mutate: fetchEmailStatus} = statusMutation;

  React.useEffect(() => {
    if (reloadKey <= 0 || !token) {
      return;
    }
    fetchEmailStatus();
  }, [fetchEmailStatus, reloadKey, token]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="flex flex-column gap-2">
        <Field
          form={form}
          name="email"
          validators={{
            onChange: ({value}) => required(value) ?? emailFormat(value),
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Email</label>
              <div className="flex gap-2">
                <InputText
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  type="email"
                  className="flex-1"
                />
                <Subscribe<[string]>
                  selector={(state) => [state.values.email]}
                >
                  {([email]) => {
                    const emailError = required(email) ?? emailFormat(email);
                    return (
                      <Button
                        type="button"
                        disabled={Boolean(emailError) || setEmailMutation.isPending}
                        loading={setEmailMutation.isPending}
                        onClick={async () => {
                          if (emailError) {
                            return;
                          }
                          await setEmailMutation.mutateAsync({email});
                        }}
                      >
                        Сменить
                      </Button>
                    );
                  }}
                </Subscribe>
              </div>
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        {emailVerified ? (
          <div>Адрес подтвержден</div>
        ) : (
          <>
            <Field
              form={form}
              name="code"
              validators={{
                onChange: ({value}) => required(value),
              }}
            >
              {(field) => (
                <div className="flex flex-column gap-2">
                  <label htmlFor={field.name}>Код</label>
                  <div className="flex gap-2">
                    <InputText
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="flex-1"
                    />
                    <Subscribe<[string]>
                      selector={(state) => [state.values.email]}
                    >
                      {([email]) => {
                        const emailError = required(email) ?? emailFormat(email);
                        const isDisabled = Boolean(emailError) || cooldown > 0 || resendMutation.isPending;
                        const label = cooldown > 0 ? `Запросить код (${cooldown})` : 'Запросить код';
                        return (
                          <Button
                            type="button"
                            disabled={isDisabled}
                            loading={resendMutation.isPending}
                            onClick={async () => {
                              if (emailError) {
                                return;
                              }
                              setCooldown(60);
                              await resendMutation.mutateAsync({email});
                            }}
                          >
                            {label}
                          </Button>
                        );
                      }}
                    </Subscribe>
                  </div>
                  {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                    <small className="p-error">{field.state.meta.errors[0]}</small>
                  ) : null}
                </div>
              )}
            </Field>
            <div className="mt-2" />
            <Subscribe<[boolean, boolean]>
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <div className="flex flex-column gap-2">
                  <Button
                    type="submit"
                    disabled={!canSubmit}
                    loading={isSubmitting || confirmMutation.isPending}
                  >
                    Подтвердить email
                  </Button>
                </div>
              )}
            </Subscribe>
          </>
        )}
      </div>
    </form>
  );
}
